/**
 * Task 16 — serial outbox push (docs/08 §4).
 * Optimistic concurrency, payload-hash idempotent retry, backoff, permanent failure.
 * Compound-cursor pull is Task 17.
 */

import type { CardZappDB } from '../db/database';
import { payloadHash } from '../db/canonicalize';
import { APPEND_ONLY_TABLES, IMMUTABLE_TABLES } from '../repositories/entityRegistry';
import type { RemoteRepository } from '../repositories/remoteRepository';
import { RemoteRepositoryError } from '../repositories/remoteErrors';
import type { OutboxEntry, SyncConflict, SyncedTableName } from '../schemas/local-only';
import { MAX_PUSH_ATTEMPTS, pushBackoffMs } from './backoff';
import { compareOutboxFlushOrder } from './flushOrder';

export type FlushOutboxResult = {
  flushed: number;
  conflicts: number;
  permanentFailures: number;
  transientFailures: number;
  /** Suggested delay before the next flushOutbox call. */
  retryAfterMs: number;
};

export type FlushOutboxArgs = {
  db: CardZappDB;
  remote: RemoteRepository;
  nowIso: string;
};

type LocalSyncRow = {
  _localDirty?: boolean;
  _baseServerUpdatedAt?: string | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
};

function isAppendOnlyBarrier(table: SyncedTableName, op: OutboxEntry['op']): boolean {
  if (op === 'insert') return false;
  return APPEND_ONLY_TABLES.has(table) || IMMUTABLE_TABLES.has(table);
}

async function hashesMatch(
  serverRow: Record<string, unknown>,
  expectedHash: string,
): Promise<boolean> {
  const actual = await payloadHash(serverRow);
  return actual === expectedHash;
}

async function markSuccess(
  db: CardZappDB,
  entry: OutboxEntry,
  serverRow: Record<string, unknown>,
): Promise<void> {
  const updatedAt = serverRow['updated_at'];
  const createdAt = serverRow['created_at'];
  if (typeof updatedAt !== 'string') {
    throw new Error('flushOutbox: server row missing updated_at');
  }

  const table = db.table(entry.table);
  await db.transaction('rw', [db.outbox, table], async () => {
    await db.outbox.delete(entry.id);
    const local = (await table.get(entry.rowId)) as LocalSyncRow | undefined;
    if (local === undefined) return;
    const patch: LocalSyncRow = {
      _localDirty: false,
      _baseServerUpdatedAt: updatedAt,
      updated_at: updatedAt,
    };
    if (typeof createdAt === 'string') {
      patch.created_at = createdAt;
    }
    if (entry.op === 'delete' && typeof serverRow['deleted_at'] === 'string') {
      patch.deleted_at = serverRow['deleted_at'];
    }
    await table.update(entry.rowId, patch);
  });
}

async function writeConflict(
  db: CardZappDB,
  entry: OutboxEntry,
  serverRow: Record<string, unknown> | null,
  nowIso: string,
): Promise<void> {
  const conflict: SyncConflict = {
    id: crypto.randomUUID(),
    table: entry.table,
    rowId: entry.rowId,
    localRow: entry.payload,
    serverRow: serverRow ?? {},
    createdAt: nowIso,
  };
  await db.transaction('rw', [db.outbox, db.sync_conflicts], async () => {
    await db.sync_conflicts.put(conflict);
    await db.outbox.put({
      ...entry,
      attempts: MAX_PUSH_ATTEMPTS,
      lastError: 'SYNC_CONFLICT',
    });
  });
}

async function markAttemptFailure(
  db: CardZappDB,
  entry: OutboxEntry,
  code: string,
): Promise<number> {
  const attempts = entry.attempts + 1;
  await db.outbox.put({
    ...entry,
    attempts,
    lastError: code,
  });
  return attempts;
}

async function resolveInsertConflict(
  db: CardZappDB,
  remote: RemoteRepository,
  entry: OutboxEntry,
  nowIso: string,
): Promise<'flushed' | 'conflict' | 'permanent' | 'transient'> {
  const serverRow = await remote.selectById(entry.table, entry.rowId);
  if (serverRow === null) {
    const attempts = await markAttemptFailure(db, entry, 'conflict_missing_row');
    return attempts >= MAX_PUSH_ATTEMPTS ? 'permanent' : 'transient';
  }
  if (await hashesMatch(serverRow, entry.payloadHash)) {
    await markSuccess(db, entry, serverRow);
    return 'flushed';
  }
  await writeConflict(db, entry, serverRow, nowIso);
  return 'conflict';
}

async function resolveZeroRowWrite(
  db: CardZappDB,
  remote: RemoteRepository,
  entry: OutboxEntry,
  nowIso: string,
): Promise<'flushed' | 'conflict'> {
  const serverRow = await remote.selectById(entry.table, entry.rowId);
  if (serverRow === null) {
    await writeConflict(db, entry, null, nowIso);
    return 'conflict';
  }
  if (await hashesMatch(serverRow, entry.payloadHash)) {
    await markSuccess(db, entry, serverRow);
    return 'flushed';
  }
  await writeConflict(db, entry, serverRow, nowIso);
  return 'conflict';
}

async function pushOne(
  db: CardZappDB,
  remote: RemoteRepository,
  entry: OutboxEntry,
  nowIso: string,
): Promise<'flushed' | 'conflict' | 'permanent' | 'transient'> {
  if (entry.attempts >= MAX_PUSH_ATTEMPTS) {
    return 'permanent';
  }

  if (isAppendOnlyBarrier(entry.table, entry.op)) {
    await db.outbox.put({
      ...entry,
      attempts: MAX_PUSH_ATTEMPTS,
      lastError: 'APPEND_ONLY_VIOLATION',
    });
    return 'permanent';
  }

  try {
    if (entry.op === 'insert') {
      try {
        const serverRow = await remote.insert(entry.table, entry.payload);
        await markSuccess(db, entry, serverRow);
        return 'flushed';
      } catch (err) {
        if (err instanceof RemoteRepositoryError && err.code === 'conflict') {
          return resolveInsertConflict(db, remote, entry, nowIso);
        }
        throw err;
      }
    }

    if (entry.op === 'update') {
      if (entry.baseServerUpdatedAt === null) {
        await db.outbox.put({
          ...entry,
          attempts: MAX_PUSH_ATTEMPTS,
          lastError: 'MISSING_BASE_VERSION',
        });
        return 'permanent';
      }
      try {
        const serverRow = await remote.update(entry.table, entry.rowId, entry.payload, {
          expectedUpdatedAt: entry.baseServerUpdatedAt,
        });
        await markSuccess(db, entry, serverRow);
        return 'flushed';
      } catch (err) {
        if (err instanceof RemoteRepositoryError && err.code === 'not_found') {
          return resolveZeroRowWrite(db, remote, entry, nowIso);
        }
        throw err;
      }
    }

    // delete → soft tombstone with optimistic concurrency
    if (entry.baseServerUpdatedAt === null) {
      await db.outbox.put({
        ...entry,
        attempts: MAX_PUSH_ATTEMPTS,
        lastError: 'MISSING_BASE_VERSION',
      });
      return 'permanent';
    }
    try {
      const serverRow = await remote.softDelete(entry.table, entry.rowId, nowIso, {
        expectedUpdatedAt: entry.baseServerUpdatedAt,
      });
      await markSuccess(db, entry, serverRow);
      return 'flushed';
    } catch (err) {
      if (err instanceof RemoteRepositoryError && err.code === 'not_found') {
        return resolveZeroRowWrite(db, remote, entry, nowIso);
      }
      throw err;
    }
  } catch (err) {
    const code =
      err instanceof RemoteRepositoryError
        ? err.code
        : err instanceof Error
          ? 'unknown'
          : 'unknown';
    // Never put financial values into lastError — codes/identifiers only.
    const attempts = await markAttemptFailure(db, entry, code);
    if (attempts >= MAX_PUSH_ATTEMPTS) return 'permanent';
    return 'transient';
  }
}

/**
 * Flush all eligible outbox entries serially in FK order.
 * Each entry is attempted at most once per call; the caller should wait
 * `retryAfterMs` before invoking again when transient failures remain.
 */
export async function flushOutbox(args: FlushOutboxArgs): Promise<FlushOutboxResult> {
  const { db, remote, nowIso } = args;
  const pending = (await db.outbox.toArray()).sort(compareOutboxFlushOrder);

  let flushed = 0;
  let conflicts = 0;
  let permanentFailures = 0;
  let transientFailures = 0;
  let retryAfterMs = 0;

  for (const entry of pending) {
    const outcome = await pushOne(db, remote, entry, nowIso);
    if (outcome === 'flushed') {
      flushed += 1;
    } else if (outcome === 'conflict') {
      conflicts += 1;
      permanentFailures += 1;
    } else if (outcome === 'permanent') {
      permanentFailures += 1;
    } else {
      transientFailures += 1;
      const refreshed = await db.outbox.get(entry.id);
      const attempts = refreshed?.attempts ?? entry.attempts + 1;
      retryAfterMs = Math.max(retryAfterMs, pushBackoffMs(attempts));
    }
  }

  return {
    flushed,
    conflicts,
    permanentFailures,
    transientFailures,
    retryAfterMs,
  };
}

/** Entries that have stopped retrying (docs/08 §4 permanent failure). */
export async function listPermanentPushFailures(db: CardZappDB): Promise<readonly OutboxEntry[]> {
  const all = await db.outbox.toArray();
  return all.filter((e) => e.attempts >= MAX_PUSH_ATTEMPTS).sort(compareOutboxFlushOrder);
}

export type SyncPushStatus = {
  pendingCount: number;
  permanentFailures: readonly OutboxEntry[];
  conflictCount: number;
};

export async function getSyncPushStatus(db: CardZappDB): Promise<SyncPushStatus> {
  const permanentFailures = await listPermanentPushFailures(db);
  const pendingCount = await db.outbox.count();
  const conflictCount = await db.sync_conflicts.count();
  return { pendingCount, permanentFailures, conflictCount };
}
